<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const cleanToolName = name => {
  return name
    .replace(/^custom_/, '')
    .replace(/^(meo|shopify|stayai|returngo|trackingmore)_/, '$1: ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const chartData = computed(() => {
  if (!props.data?.length) return [];
  const maxCount = props.data[0]?.count || 1;
  return props.data.slice(0, 10).map(item => ({
    ...item,
    displayName: cleanToolName(item.tool),
    pct: ((item.count / maxCount) * 100).toFixed(0),
  }));
});
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11 uppercase tracking-wider">
      {{ t('CEO_DASHBOARD.TOOL_USAGE.HEADER') }}
    </h3>
    <div v-if="loading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <div
      v-else-if="chartData.length === 0"
      class="flex items-center justify-center py-8"
    >
      <span class="text-sm text-n-slate-10">
        {{ t('CEO_DASHBOARD.BREAKDOWN.NO_DATA') }}
      </span>
    </div>
    <div v-else class="space-y-2">
      <div
        v-for="item in chartData"
        :key="item.tool"
        class="flex items-center gap-3"
      >
        <span
          class="text-xs text-n-slate-11 w-32 truncate text-right flex-shrink-0"
          :title="item.displayName"
        >
          {{ item.displayName }}
        </span>
        <div class="flex-1 h-5 rounded bg-n-alpha-1 overflow-hidden">
          <div
            class="h-full rounded bg-n-blue-9 transition-all duration-300"
            :style="{ width: item.pct + '%' }"
          />
        </div>
        <span
          class="text-xs font-medium text-n-slate-12 w-8 text-right flex-shrink-0"
        >
          {{ item.count }}
        </span>
      </div>
    </div>
  </div>
</template>
